import React, { useState, useEffect, useCallback } from 'react';
// import MUI components
import { Grid2 as Grid, Alert, Snackbar } from '@mui/material';
// import custom components
import { BoxCenterStart } from '../style/box';
import { TypographyBold } from '../style/typography';
import { commonStyles } from "../style/common";
import CommonCard from '../components/common_card';
import { BaseDialog } from '../components/base_dialog';
import WorkRegistration from "../components/work_registration";
import WorkList from '../components/work_list';
import { validationSchemaRegistWork, validationSchemaEditWork } from '../func/validation_check';
import { fetchData, postData } from "../func/api";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { DragDropContext } from '@hello-pangea/dnd';
import SkeletonLoading from "../components/skeleton_loading";
import { REPORT_STATUS } from '../const/config';

const categories = [
    { value: "1", label: "ダム関連業務" },
    { value: "2", label: "発電所関連業務" }
];

const MstWorkList = () => {
    const [workList, setWorkList] = useState([]);
    const [damWorkList, setDamWorkList] = useState([]);
    const [powerplantWorkList, setPowerplantWorkList] = useState([]);
    const [workCategories, setWorkCategories] = useState(categories);
    const [expandedWork, setExpandedWork] = useState(false);
    const [editWork, setEditWork] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [workToDelete, setWorkToDelete] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogType, setDialogType] = useState(null);
    const [dialogData, setDialogData] = useState(null);
    const [alertMessage, setAlertMessage] = useState("");
    const [alertOpen, setAlertOpen] = useState(false);
    const [alertSeverity, setAlertSeverity] = useState("success");
    const [refreshFlag, setRefreshFlag] = useState(false);
    const [loading, setLoading] = useState(true);
    const [isDisabled, setIsDisabled] = useState(false);

    const { control: registControl, handleSubmit: handleRegistSubmit, reset: registReset } = useForm({
        resolver: yupResolver(validationSchemaRegistWork()),
        defaultValues: {
            registWork: {
                name: "",
                category: "1"
            }
        },
        mode: "onBlur"
    });

    const { control: editControl, handleSubmit: handleEditSubmit, reset: editReset } = useForm({
        resolver: yupResolver(validationSchemaEditWork),
        defaultValues: {
            editWork: {
                name: ""
            }
        },
        mode: "onBlur"
    });

    // データ取得
    useEffect(() => {
        const fetchDataAll = async () => {
            try {
                const [workList, damWorkList, powerplantWorkList] = await Promise.all([
                    fetchData(`api/get_work_list`),
                    fetchData(`api/get_category_work_list/1`),
                    fetchData(`api/get_category_work_list/2`)
                ]);

                setWorkList(workList); // 業務一覧
                setDamWorkList(damWorkList); // ダム関連業務一覧
                setPowerplantWorkList(powerplantWorkList); // 発電所関連業務一覧

                // 地震一覧データ取得
                const earthquakeData = await fetchData(`api/get_earthquake_list/`);
                // 業務対中の場合は、編集不可
                const reportingData = earthquakeData.find(obj =>
                    obj.report_status_uuid === REPORT_STATUS.UNSUPPORTED ||
                    obj.report_status_uuid === REPORT_STATUS.FISRTREPORT ||
                    obj.report_status_uuid === REPORT_STATUS.PRIMARYCHECK ||
                    obj.report_powerplant_status_uuid === REPORT_STATUS.POWERPLANTSUPPORT
                );
                setIsDisabled(reportingData ? true : false);
            } catch (error) {
                console.error("業務一覧取得中にエラーが発生しました:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchDataAll();
    }, [refreshFlag]);

    // 編集対象の業務が変わったらフォームをリセット
    useEffect(() => {
        if (editWork) {
            editReset({
                editWork: {
                    uuid: editWork.uuid,
                    name: editWork.name,
                    category: editWork.category
                }
            });
        }
    }, [editWork, editReset]);

    const openDialog = (type, data = null) => {
        setDialogType(type);
        setDialogData(data);
        setDialogOpen(true);
    };

    const handleCancelEdit = () => {
        setExpandedWork(false);
        setEditWork(null);
    };

    const handleDialogConfirm = () => {
        switch (dialogType) {
            case 'register':
                handleConfirm(dialogData); // 登録処理
                break;
            case 'delete':
                handleDeleteConfirm(); // 削除処理
                break;
            case 'save':
                handleSaveConfirm(); // 保存処理
                break;
            default:
                break;
        }
        setDialogOpen(false);
    };

    const handleDialogCancel = () => {
        setDialogOpen(false);
    };

    // 「業務名を登録」ボタンのダイアログ「OK」押下時
    const handleConfirm = async (formData) => {
        try {
            // 登録する業務のカテゴリー
            const category = Number(formData.category);

            // 対象カテゴリーのsort_numだけを抽出
            const categorySortNums = workList
                .filter(w => Number(w.category) === category)
                .map(w => Number(w.sort_num) || 0);

            const maxSortNum = categorySortNums.length > 0
                ? Math.max(...categorySortNums)
                : 0;

            const nextSortNum = maxSortNum + 1;

            const postDataObj = {
                name: formData.name,
                sort_num: nextSortNum,
                category: category
            };

            await postData("api/post_regist_work", postDataObj);

            setAlertMessage("登録が完了しました");
            setAlertSeverity("success");
            setAlertOpen(true);
            setRefreshFlag(prev => !prev);
            registReset();
        } catch (err) {
            console.error(err);
            setAlertMessage("登録に失敗しました");
            setAlertSeverity("error");
            setAlertOpen(true);
        }
    };
    const handleEditClick = (work) => {
        setEditWork({ ...work });
        setExpandedWork(work.uuid);
    };

    const handleDeleteClick = (work) => {
        // 削除対象がダム業務の場合
        if (Number(work.category) === 1 && damWorkList.length === 1) {
            setAlertMessage("ダム関連業務は最低1つ必要です。");
            setAlertSeverity("error");
            setAlertOpen(true);
            return;
        }
        // 削除対象が発電所業務の場合
        if (Number(work.category) === 2 && powerplantWorkList.length === 1) {
            setAlertMessage("発電所関連業務は最低1つ必要です。");
            setAlertSeverity("error");
            setAlertOpen(true);
            return;
        }
        openDeleteDialog(work);
        setWorkToDelete(work);
        setDeleteDialogOpen(true);
    };

    const openDeleteDialog = (work) => {
        setDialogType("delete");
        setDialogData(work); // 削除対象
        setDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        try {
            // 削除前の業務情報を履歴テーブルに登録
            await postData("api/post_insert_work_history", workToDelete);
            const targetCategory = workToDelete.category; // 削除対象のカテゴリ

            // 同一カテゴリのリストだけを対象に削除・詰め直し
            const updatedCategoryList = workList
                .filter(w => Number(w.category) === Number(targetCategory) && w.uuid !== workToDelete.uuid)
                .sort((a, b) => a.sort_num - b.sort_num)
                .map((w, index) => ({ ...w, sort_num: index + 1 }));

            // 他カテゴリはそのまま
            const untouchedList = workList.filter(w => Number(w.category) !== Number(targetCategory));

            // 結合して最終更新リストに
            const updatedList = [...untouchedList, ...updatedCategoryList];

            // DB 更新（削除対象カテゴリ分だけ sort_num を更新）
            for (const work of updatedCategoryList) {
                await postData("api/post_update_work_sort_num", {
                    uuid: work.uuid,
                    sort_num: work.sort_num
                });
            }

            // 削除API実行（論理削除）
            await postData("api/post_delete_work", { uuid: workToDelete.uuid });

            // state 更新
            setDamWorkList(updatedList.filter(w => Number(w.category) === 1).sort((a, b) => a.sort_num - b.sort_num));
            setPowerplantWorkList(updatedList.filter(w => Number(w.category) === 2).sort((a, b) => a.sort_num - b.sort_num));

            setAlertMessage("業務の削除と並び順の更新が完了しました。");
            setAlertSeverity("success");
            setAlertOpen(true);
        } catch (err) {
            console.error("削除失敗しました：", err);
            setAlertMessage("業務の削除に失敗しました。再度お試しください。");
            setAlertSeverity("error");
            setAlertOpen(true);
        } finally {
            setDeleteDialogOpen(false);
            setWorkToDelete(null);
            setExpandedWork(false);
            setEditWork(null);
            setRefreshFlag(prev => !prev);
        }
    };

    const handleSaveConfirm = async () => {
        try {
            const updatedData = {
                ...dialogData,
                category: Number(dialogData.category)
            };

            // 更新前のデータを取得
            const originalWork = workList.find(w => w.uuid === updatedData.uuid);
            if (originalWork) {
                // 履歴テーブルに登録（変更前データ）
                await postData("api/post_insert_work_history", originalWork);
            }

            // 旧カテゴリーと新カテゴリー
            const oldCategory = Number(originalWork.category);
            const newCategory = Number(updatedData.category);

            // 旧カテゴリと新カテゴリのリスト
            const oldCategoryList = workList
                .filter(w => Number(w.category) === oldCategory && w.uuid !== updatedData.uuid)
                .sort((a, b) => a.sort_num - b.sort_num);

            const newCategoryList = workList
                .filter(w => Number(w.category) === newCategory)
                .sort((a, b) => a.sort_num - b.sort_num);

            // カテゴリーが変更された場合
            if (oldCategory !== newCategory) {
                // --- ① 旧カテゴリー: sort_numを詰め直す ---
                const reindexedOldList = oldCategoryList.map((w, index) => ({
                    ...w,
                    sort_num: index + 1
                }));

                // --- ② 新カテゴリー: 最大sort_num + 1を割り当てる ---
                const maxSortNumNewCat = newCategoryList.length > 0
                    ? Math.max(...newCategoryList.map(w => Number(w.sort_num) || 0))
                    : 0;
                updatedData.sort_num = maxSortNumNewCat + 1;

                // --- DB 更新 ---
                // 旧カテゴリのsort_numを更新
                for (const work of reindexedOldList) {
                    await postData("api/post_update_work_sort_num", {
                        uuid: work.uuid,
                        sort_num: work.sort_num
                    });
                }

            } else {
                // カテゴリー変更なしの場合、sort_numはそのまま
                updatedData.sort_num = originalWork.sort_num;
            }

            // --- 対象業務を更新 ---
            await postData("api/post_update_work", updatedData);

            // stateを更新（カテゴリ順＋sort_num順でソート）
            setWorkList(prev => {
                const newList = prev.map(r => r.uuid === updatedData.uuid ? updatedData : r);
                return newList.sort((a, b) => {
                    if (a.category !== b.category) return a.category - b.category;
                    return a.sort_num - b.sort_num;
                });
            });

            setAlertMessage("業務の更新が完了しました。");
            setAlertSeverity("success");
            setAlertOpen(true);
            setRefreshFlag(prev => !prev);
        } catch (err) {
            console.error("更新失敗しました：", err);
            setAlertMessage("業務の更新に失敗しました。");
            setAlertSeverity("error");
            setAlertOpen(true);
        } finally {
            setExpandedWork(false);
            setEditWork(null);
            setDialogOpen(false);
        }
    };

    const onDragEnd = useCallback(async (result) => {
        const { source, destination } = result;
        if (!destination || source.droppableId !== destination.droppableId) return;

        // ドロップ対象のカテゴリ判定
        let category;
        if (source.droppableId === "dam-work") {
            category = 1;
        } else if (source.droppableId === "powerplant-work") {
            category = 2;
        } else {
            return;
        }

        // 対象カテゴリのリストのみ取り出す
        const targetList = workList
            .filter(w => Number(w.category) === category)
            .sort((a, b) => a.sort_num - b.sort_num);

        // 並び替え関数
        const reorder = (list, startIndex, endIndex) => {
            const result = Array.from(list);
            const [removed] = result.splice(startIndex, 1);
            result.splice(endIndex, 0, removed);
            return result;
        };

        // 並び替え後のリストにsort_numを再付与
        const reordered = reorder(targetList, source.index, destination.index)
            .map((w, index) => ({ ...w, sort_num: index + 1 }));

        // 他カテゴリはそのまま
        const untouched = workList.filter(w => Number(w.category) !== category);

        // 新しい全体リスト
        const updatedList = [...untouched, ...reordered].sort((a, b) => {
            if (a.category !== b.category) return a.category - b.category;
            return a.sort_num - b.sort_num;
        });

        // workList 更新
        setWorkList(updatedList);
        // カテゴリ別リストも更新
        setDamWorkList(updatedList.filter(w => Number(w.category) === 1).sort((a, b) => a.sort_num - b.sort_num));
        setPowerplantWorkList(updatedList.filter(w => Number(w.category) === 2).sort((a, b) => a.sort_num - b.sort_num));

        // --- DB更新 ---
        try {
            for (const work of reordered) {
                await postData("api/post_update_work_sort_num", {
                    uuid: work.uuid,
                    sort_num: work.sort_num
                });
            }
            setAlertMessage("業務の並び順を更新しました。");
            setAlertSeverity("success");
            setAlertOpen(true);
        } catch (err) {
            console.error("並び順更新に失敗しました：", err);
            setAlertMessage("並び順の更新に失敗しました。再度お試しください。");
            setAlertSeverity("error");
            setAlertOpen(true);
        }
    }, [workList]);

    if (loading) {
        return <SkeletonLoading />;
    }

    return (
        <BoxCenterStart sx={commonStyles.page}>
            <Grid
                container
                spacing={7}
                columns={12}
                sx={commonStyles.container}
            >
                {/* Page Title */}
                <Grid size={{ xs: 12 }}>
                    <CommonCard
                        size={{ xs: 12 }}
                        child={
                            <TypographyBold
                                sx={{ textAlign: 'center', padding: '10px 0' }}
                            >
                                業務管理
                            </TypographyBold>
                        }
                    ></CommonCard>
                </Grid>
                {/* 新規業務登録 */}
                <WorkRegistration
                    control={registControl}
                    workCategories={workCategories}
                    openDialog={openDialog}
                    handleRegistSubmit={handleRegistSubmit}
                    expanded={true}
                    isDisabled={isDisabled}
                />
                <DragDropContext onDragEnd={onDragEnd}>
                    {/* ダム業務一覧 */}
                    <WorkList
                        workList={damWorkList}
                        workCategories={workCategories}
                        control={editControl}
                        expandedWork={expandedWork}
                        handleEditClick={handleEditClick}
                        handleCancelEdit={handleCancelEdit}
                        handleDeleteClick={handleDeleteClick}
                        handleEditSubmit={handleEditSubmit}
                        openDialog={openDialog}
                        title="ダム関連業務一覧"
                        droppableId="dam-work"
                        isDisabled={isDisabled}
                    />
                    {/* 発電所業務一覧 */}
                    <WorkList
                        workList={powerplantWorkList}
                        workCategories={workCategories}
                        control={editControl}
                        expandedWork={expandedWork}
                        handleEditClick={handleEditClick}
                        handleCancelEdit={handleCancelEdit}
                        handleDeleteClick={handleDeleteClick}
                        handleEditSubmit={handleEditSubmit}
                        openDialog={openDialog}
                        title="発電所関連業務一覧"
                        droppableId="powerplant-work"
                        isDisabled={isDisabled}
                    />
                </DragDropContext>
                {/* 確認ダイアログ */}
                <BaseDialog
                    open={dialogOpen}
                    onClose={handleDialogCancel}
                    title={
                        dialogType === "register" ? "業務登録" :
                            dialogType === "delete" ? "業務削除" :
                                dialogType === "save" ? "業務保存" : ""
                    }
                    message={
                        dialogType === "register" ? "業務を登録します。よろしいですか？" :
                            dialogType === "delete" ? `業務名「${dialogData?.name}」を削除しますか？\nこの操作は取り消せません。` :
                                dialogType === "save" ? "編集内容を保存します。よろしいですか？" : ""
                    }
                    onConfirm={handleDialogConfirm}
                    confirmText={
                        dialogType === "register" ? "OK" :
                            dialogType === "delete" ? "削除" :
                                dialogType === "save" ? "保存" : "OK"
                    }
                    cancelText="キャンセル"
                />
                {/* 失敗時のSnackbar */}
                {alertMessage && (
                    <Snackbar
                        open={alertOpen}
                        autoHideDuration={3000}
                        onClose={() => setAlertOpen(false)}
                        anchorOrigin={{ vertical: "top", horizontal: "center" }}
                    >
                        <Alert severity={alertSeverity} onClose={() => setAlertOpen(false)}>
                            {alertMessage}
                        </Alert>
                    </Snackbar>
                )}
            </Grid>
        </BoxCenterStart>
    );
};

export default MstWorkList;